import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Lock, PauseCircle } from 'lucide-react';
import { CIRCOLARI_IN_ARRIVO } from '../utils/circolariInArrivo';
import ElencoCircolari, { formatDataCircolare } from '../components/circolari/ElencoCircolari';
import {
  CircolariApi,
  getAnniCircolari,
  getCircolare,
  getCircolari,
  isCircolariDisattivate,
  isNonInRegola,
  openAllegatoCircolare,
  scaricaAllegatoCircolare,
} from '../services/circolariService';

export { formatDataCircolare };

/**
 * Chiamate dell'archivio associati. Costruito dalle esportazioni del servizio
 * (non con creaCircolariApi) perché i test delle pagine mockano proprio quelle.
 */
export const API_CIRCOLARI_ASSOCIATO: CircolariApi = {
  getCircolari: (...a) => getCircolari(...a),
  getAnniCircolari: () => getAnniCircolari(),
  getCircolare: (id) => getCircolare(id),
  openAllegatoCircolare: (id, allegato) => openAllegatoCircolare(id, allegato),
  scaricaAllegatoCircolare: (id, allegato) => scaricaAllegatoCircolare(id, allegato),
};

const CARD = 'rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800';

/** Torna all'area riservata: stessa intestazione nelle due pagine. */
export const BackToAreaRiservata: React.FC<{ label?: string; to?: string }> = ({
  label = 'Area Riservata',
  to = '/area-riservata',
}) => {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate(to)}
      className="inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-4 transition-colors"
    >
      <ArrowLeft className="w-3.5 h-3.5" />
      {label}
    </button>
  );
};

/** Riquadro a pagina intera per chi non è in regola con la quota (403 NON_IN_REGOLA). */
export const AvvisoNonInRegola: React.FC = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <BackToAreaRiservata />
        <div role="alert" className={`${CARD} text-center px-6 py-14`}>
          <span className="w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center bg-amber-100 dark:bg-amber-900/40">
            <Lock className="w-5 h-5 text-amber-600 dark:text-amber-400" aria-hidden="true" />
          </span>
          <h1 className="text-base font-semibold text-gray-900 dark:text-white">
            Le circolari sono riservate agli associati in regola con la quota
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 max-w-md mx-auto">
            Rinnova la quota associativa per consultare l&apos;archivio delle circolari e scaricare gli allegati.
            Nel tuo profilo trovi lo stato del tesseramento e come mettersi in regola.
          </p>
          <button
            type="button"
            onClick={() => navigate('/area-riservata?tab=profilo')}
            className="inline-flex items-center gap-1.5 mt-6 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            Vai al mio profilo
          </button>
        </div>
      </div>
    </div>
  );
};

/** Riquadro a pagina intera quando il modulo circolari è spento dal server (503). */
export const AvvisoCircolariDisattivate: React.FC = () => (
  <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
      <BackToAreaRiservata />
      <div role="status" className={`${CARD} text-center px-6 py-14`}>
        <span className="w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center bg-gray-100 dark:bg-gray-700">
          <PauseCircle className="w-5 h-5 text-gray-500 dark:text-gray-300" aria-hidden="true" />
        </span>
        <h1 className="text-base font-semibold text-gray-900 dark:text-white">
          Le circolari sono temporaneamente disattivate
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 max-w-md mx-auto">
          L&apos;archivio delle circolari al momento non è disponibile. Riprova più tardi.
        </p>
      </div>
    </div>
  </div>
);

const CircolariPage: React.FC = () => {
  const [nonInRegola, setNonInRegola] = useState(false);
  const [disattivate, setDisattivate] = useState(false);

  const onErroreAccesso = (error: any): boolean => {
    if (isNonInRegola(error)) { setNonInRegola(true); return true; }
    if (isCircolariDisattivate(error)) { setDisattivate(true); return true; }
    return false;
  };

  if (nonInRegola) return <AvvisoNonInRegola />;
  if (disattivate) return <AvvisoCircolariDisattivate />;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <BackToAreaRiservata />

        <div className="flex items-start gap-3">
          <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-indigo-100 dark:bg-indigo-900/40">
            <FileText className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
          </span>
          <div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Comunicati e norme</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Circolari, comunicati e normativa UNASCA: apri una voce per leggerla e scaricare gli allegati
            </p>
          </div>
        </div>

        <div className="mt-5">
          <ElencoCircolari
            api={API_CIRCOLARI_ASSOCIATO}
            linkDettaglio={(id) => `/area-riservata/circolari/${id}`}
            onErroreAccesso={onErroreAccesso}
          />
        </div>
      </div>
    </div>
  );
};

// Finché l'archivio è «in arrivo» la pagina rimanda alla dashboard (vedi utils/circolariInArrivo.ts)
const CircolariPageGuard: React.FC = () =>
  CIRCOLARI_IN_ARRIVO ? <Navigate to="/area-riservata" replace /> : <CircolariPage />;

export default CircolariPageGuard;
